import { Injectable } from '@angular/core';
import {environment} from "../../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {LocalStorageService} from "../local-storage/local-storage.service";
import {BehaviorSubject, Observable, of, tap} from "rxjs";
import {InternalUser} from "../../models/internal-user.model";
import {ActivatedRouteSnapshot} from "@angular/router";
import { CreatePassword } from '../../models/createNewPassword';
import { ModulePermission } from '../../models/modulePermission/modulePermission';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private apiUrl = `${environment.apiUrl}/internal`;
  private readonly TOKEN_NAME: string = 'appToken';
   private readonly USERID_NAME: string = 'appUserId';
  private readonly USERNAME_NAME: string = 'appUserName';
    private readonly USERGROUP_NAME: string = 'appUseGroup';
  private readonly MODULES_NAME: string = 'appModules';
  private userSubject = new BehaviorSubject<InternalUser | undefined>(undefined);
  private emailToReset: string | undefined;

  constructor(private http: HttpClient,
              private readonly localStorage: LocalStorageService) { }

  loginUser(username: string, password: string): Observable<InternalUser> {
    const url = `${this.apiUrl}/authenticate`;
    return this.http.post<InternalUser>(url, { username, password })
      .pipe(tap(user => {
        if (user) {
          // Backend returns `id` as a numeric Long. Normalize to string so it
          // matches the localStorage-restored shape (`id?.toString()`) and is
          // safe to use as an HTTP header value (HttpHeaders spreads non-strings
          // and throws on numbers). Without this, the first action after login
          // (e.g. processPayment -> X-User-Id header) crashes in the interceptor.
          if (user.id != null) {
            user.id = String(user.id);
          }
          this.localStorage.set(this.TOKEN_NAME, user.token);
          this.localStorage.set(this.USERNAME_NAME, user.username);
          this.localStorage.set(this.USERID_NAME, user.id);
          this.localStorage.set(this.USERGROUP_NAME, user.userGroupId);
          this.localStorage.set(this.MODULES_NAME ,JSON.stringify(user.modules))
          this.userSubject.next(user);
        }
      }));
  }

  /** Swaps the stored JWT (and grants) for a fresh one; see SessionService. */
  refreshSession(): Observable<InternalUser> {
    return this.http.post<InternalUser>(`${environment.apiUrl}/api/admin/session/refresh`, {})
      .pipe(tap(user => {
        if (user?.token) {
          this.localStorage.set(this.TOKEN_NAME, user.token);
          this.localStorage.set(this.MODULES_NAME, JSON.stringify(user.modules));
        }
      }));
  }

  validateToken(): Observable<boolean> {
    const token = this.localStorage.get(this.TOKEN_NAME)
    return token && token.length > 0;
  }

  validateAuthorization(route: ActivatedRouteSnapshot, token: string): Observable<boolean>{
    return of(token === "foo");
  }

  getCurrentUser(): InternalUser | undefined {
    let currentUser = this.userSubject.value;
    if (!currentUser) {
      const cachedUser = this.getUserFromLocalStorage();
      if (cachedUser) {
        this.userSubject.next(cachedUser);
        currentUser = cachedUser;
      }
    }
    return currentUser;
  }

  private getUserFromLocalStorage(): InternalUser {
    // Values are written JSON-encoded (a string lands as "\"32\""), so read them back the
    // same way. Reading raw sent X-User-Id: "32" WITH quotes, the backend's Long.valueOf
    // failed, and every cash payment after a page reload was refused as "drawer closed".
    const unwrap = (raw: string | null): string | undefined => {
      if (raw == null) return undefined;
      try { const v = JSON.parse(raw); return v == null ? undefined : String(v); } catch { return raw; }
    };
    const username = unwrap(localStorage.getItem(this.USERNAME_NAME));
    const modules = this.parseModules(localStorage.getItem(this.MODULES_NAME));
    const id = unwrap(localStorage.getItem(this.USERID_NAME));
    return { username, modules: modules ?? undefined, id };
  }

  /** Safely parse the cached modules; tolerate missing/empty/malformed values. */
  private parseModules(raw: string | null): any[] {
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  logout() {
    this.localStorage.remove(this.TOKEN_NAME);
    this.localStorage.remove(this.USERNAME_NAME);
         this.localStorage.remove(this.USERID_NAME);
          this.localStorage.remove(this.MODULES_NAME);
    this.userSubject.next(undefined);
  }

  getEmailToReset(): string {
    return <string>this.emailToReset;
  }

  validateCode(email: string, code: string) {
    const url = `${this.apiUrl}/password/validate-otp/${email}`;
    return this.http.post(url, code, {responseType: 'text'});
  }

  requestPasswordReset(email: string) {
    this.emailToReset = email;
    const url = `${this.apiUrl}/password/send-otp`;
    return this.http.post(url, email, {responseType: 'text'});
  }

  changePassword(username: string | undefined, password: string) {
    const url = `${this.apiUrl}/password`;
    return this.http.put(url, { username, password }, {responseType: 'text'});
  }

   setNewPassword(model: CreatePassword) {
    const url = `${this.apiUrl}/setPassword`;
    return this.http.post(url, model, {responseType: 'text'});
  }
}
